import Phaser from "phaser";
import { getCameraResetPixelCenter } from "../core/CameraManager";
import {
  computeFollowTarget,
  computeStaticCameraPosition,
  lerpPixelSnap,
  needsOneShotCorrection,
  type CameraPixelPosition,
  type PlayerPixelPosition,
} from "../core/CameraRuntime";
import { getRuntimeSession } from "../core/RuntimeSession";
import { DefaultActionController } from "../controller/ActionController";
import { InputHandler } from "../controller/InputHandler";
import { DebugController } from "../debug/DebugController";
import type { LevelState } from "../model/LevelState";
import type { PlaybackMetadata } from "../model/ActionResolver";
import type { DebugSnapshot } from "../types/debug";
import type { GridPoint } from "../types/grid";
import type { LevelData } from "../types/level";
import { buildGuessSequence, createEmptyNoteState } from "../model/NoteState";
import type { NoteState } from "../model/NoteState";
import { DebugOverlay } from "../view/DebugOverlay";
import { LevelRenderer } from "../view/LevelRenderer";
import { EntityRenderer } from "../view/EntityRenderer";
import { FogRenderer } from "../view/FogRenderer";
import { HudRenderer } from "../view/HudRenderer";
import { SymbolSequenceRenderer } from "../view/SymbolSequenceRenderer";
import { CodeRainRenderer } from "../view/CodeRainRenderer";
import { CollectibleEffectRenderer } from "../view/CollectibleEffectRenderer";
import { BreadcrumbEffectRenderer } from "../view/BreadcrumbEffectRenderer";
import { BreadcrumbMessageRenderer } from "../view/BreadcrumbMessageRenderer";
import { LayoutBitmapTextRenderer } from "../view/LayoutBitmapTextRenderer";
import {
  ACTIVE_SIMSUN_ASCII_FONT,
  SIMSUN_ASCII_FONT_CONFIGS,
  SYMBOL_SEQUENCE_FONT_CONFIGS,
  type BitmapFontLayoutProfile,
} from "../view/SymbolSequenceFont";
import { CAMERA_RUNTIME } from "../core/CameraRuntime";
import { RENDER_PALETTE, TILE_SIZE } from "../view/render-palette";
import {
  COLLECTIBLE_SIZE,
  COLLECTIBLE_OFFSET,
  BREADCRUMB_SIZE,
  BREADCRUMB_OFFSET,
  type EntityVisibilityOverride,
} from "../view/draw-model";
import { NoteScene } from "./NoteScene";
import { ESC_TARGET_SCENE, initializeLevelScene } from "./LevelSceneSetup";
import type { LevelSceneData } from "./LevelSceneSetup";
import type { LevelSceneInitResult } from "./LevelSceneSetup";
import type { LevelSceneTransitionInData } from "./LevelSceneSetup";
import { DEFAULT_BGM_VOLUME, LEVEL0_BGM_KEY, LEVEL_BGM_KEY } from "./BootScene";
import { EndingScene } from "./EndingScene";

function createAllGridPoints(levelData: LevelData): readonly GridPoint[] {
  const points: GridPoint[] = [];

  for (let y = 0; y < levelData.grid.height; y += 1) {
    for (let x = 0; x < levelData.grid.width; x += 1) {
      points.push({ x, y });
    }
  }

  return points;
}

function createRenderSnapshot(levelState: LevelState): DebugSnapshot {
  return (
    levelState.lastDebugSnapshot ?? {
      tick: levelState.tick,
      input: { raw: "startup", mapped: null },
      result: "valid",
      reason: null,
      state: {
        player: {
          x: levelState.player.x,
          y: levelState.player.y,
          facing: levelState.player.facing,
        },
        history: {
          length: levelState.history.length,
          last5: [...levelState.history.last5],
        },
        fog: {
          explored: levelState.fog.explored.map((point) => ({
            x: point.x,
            y: point.y,
          })),
          permanentlyRevealed: levelState.fog.permanentReveal,
          debugReveal: levelState.fog.debugReveal,
        },
        collectibles: {
          local: [...levelState.collectibles.local],
          permanent: [...levelState.collectibles.permanent],
        },
        breadcrumbs: {
          active: levelState.breadcrumbs.active.map((b) => ({ ...b })),
          local: [...levelState.breadcrumbs.local],
        },
        symbolSequence: {
          raw: [...levelState.symbolSequence.raw],
          expanded: [...levelState.symbolSequence.expanded],
          playerNotes: { ...levelState.symbolSequence.playerNotes },
        },
      },
    }
  );
}

function synchronizePersistentEffects(
  levelState: LevelState,
  levelData: LevelData,
  permanentReveal: boolean,
): LevelState {
  if (!permanentReveal) {
    return levelState;
  }

  const allGridPoints = createAllGridPoints(levelData);

  return {
    ...levelState,
    fog: {
      ...levelState.fog,
      permanentReveal,
      explored: allGridPoints,
      visible: allGridPoints,
    },
  };
}

function revealFogPoints(
  levelState: LevelState,
  points: readonly GridPoint[],
): LevelState {
  if (points.length === 0) {
    return levelState;
  }

  const existingVisible = new Set(levelState.fog.visible.map(createPointKey));
  const existingExplored = new Set(levelState.fog.explored.map(createPointKey));
  const visible = [...levelState.fog.visible];
  const explored = [...levelState.fog.explored];

  for (const point of points) {
    const key = createPointKey(point);
    if (!existingVisible.has(key)) {
      visible.push(point);
      existingVisible.add(key);
    }
    if (!existingExplored.has(key)) {
      explored.push(point);
      existingExplored.add(key);
    }
  }

  return {
    ...levelState,
    fog: {
      ...levelState.fog,
      visible,
      explored,
    },
  };
}

function createPointKey(point: GridPoint): string {
  return `${point.x},${point.y}`;
}

function isSamePoint(a: GridPoint, b: GridPoint): boolean {
  return a.x === b.x && a.y === b.y;
}

interface SceneTimerHandle {
  destroy?(): void;
  remove?(): void;
}

interface PlaybackStepEffects {
  readonly collectibleIds: string[];
  readonly breadcrumbIds: string[];
}

interface WalkSettlePlaybackContext {
  readonly stepEffects: PlaybackStepEffects[];
  readonly completionEffects: PlaybackStepEffects;
  readonly pendingCollectibleIds: Set<string>;
  readonly pendingBreadcrumbIds: Set<string>;
}

interface CameraMotionState {
  readonly from: CameraPixelPosition;
  readonly to: CameraPixelPosition;
  readonly durationMs: number;
  readonly onComplete?: () => void;
  elapsedMs: number;
}

interface PerfectRevealAnimationState {
  readonly layers: readonly (readonly GridPoint[])[];
  readonly layersPerStep: number;
  nextLayerIndex: number;
  elapsedMs: number;
  completed: boolean;
  completedElapsedMs: number;
}

type EndingPhase = "undo" | "obey" | "complete";

const FOLLOW_INTRO_HOLD_MS = 500;
const FOLLOW_INTRO_MOVE_MS = 500;
const FOLLOW_CORRECTION_MOVE_MS = 500;
const STEADY_FOLLOW_LERP_FACTOR = 0.5;
const LEVEL_COMPLETION_BURST_HOLD_MS = 750;
const LEVEL_COMPLETION_FADE_OUT_MS = 500;
const LEVEL_SCENE_RESTART_FADE_IN_MS = 500;
const LEVEL0_INTER_LEVEL_FADE_IN_MS = 500;
const ENDING_INTER_LEVEL_FADE_MS = 500;
const LEVEL_COMPLETE_TO_SELECT_FADE_IN_MS = 500;
const PERFECT_REVEAL_TARGET_MS = 900;
const PERFECT_REVEAL_STEP_MS = 50;
const PERFECT_REVEAL_COMPLETED_HOLD_MS = 500;
const ENDING_INITIAL_LEVEL_ID = "ending-0";
const ENDING_FINAL_LEVEL_ID = "ending-3";
const ENDING_SEQUENCE_LENGTH = 5;
const ENDING_FINAL_FADE_OUT_MS = 3000;
const ENDING_DARKNESS_ALPHA: Readonly<Record<string, number>> = {
  "ending-1": 0.32,
  "ending-2": 0.64,
  "ending-3": 0.96,
};
const ENDING_NEXT_LEVELS: Readonly<Record<string, string>> = {
  "ending-0": "ending-1",
  "ending-1": "ending-2",
  "ending-2": ENDING_FINAL_LEVEL_ID,
};
const LEVEL0_LEGACY_ID = "level-0";
const LEVEL0_FINAL_ID = "level-0-3";
const LEVEL0_ONBOARDING_LEVEL_IDS = new Set([
  "level-0-1",
  "level-0-2",
  LEVEL0_FINAL_ID,
  LEVEL0_LEGACY_ID,
]);
const LEVEL0_WHITE_TRANSITION_TARGETS: Readonly<Record<string, string>> = {
  "level-0-1": "level-0-2",
  "level-0-2": LEVEL0_FINAL_ID,
};
const LEVEL0_SCRAMBLE_GLYPHS = ["◇", "○", "△", "▽", "□", "↺", "③", "⑤"] as const;
const LEVEL0_SCRAMBLE_INTERVAL_MS = 50;
let preserveLevel0BgmForNextScene = false;

function isLevel0OnboardingLevel(levelId: string): boolean {
  return LEVEL0_ONBOARDING_LEVEL_IDS.has(levelId);
}

function getLevel0WhiteTransitionTarget(levelId: string): string | null {
  return LEVEL0_WHITE_TRANSITION_TARGETS[levelId] ?? null;
}

function isLevel0FinalTransitionLevel(levelId: string): boolean {
  return levelId === LEVEL0_FINAL_ID || levelId === LEVEL0_LEGACY_ID;
}

function isLevel0FinalAllowedInput(code: string): boolean {
  return code === "KeyD" || code === "ArrowRight" || code === "D" || code === "d" || code === "right";
}

function isEndingLevel(levelId: string): boolean {
  return (
    levelId === ENDING_INITIAL_LEVEL_ID ||
    levelId === "ending-1" ||
    levelId === "ending-2" ||
    levelId === ENDING_FINAL_LEVEL_ID
  );
}

function getInitialEndingPhase(levelId: string): EndingPhase {
  return levelId === ENDING_INITIAL_LEVEL_ID ? "undo" : "obey";
}

function getNextEndingLevelId(levelId: string): string | null {
  return ENDING_NEXT_LEVELS[levelId] ?? null;
}

function getEndingDarknessAlpha(levelId: string): number {
  return ENDING_DARKNESS_ALPHA[levelId] ?? 0;
}

function isEndingUndoInput(code: string): boolean {
  return code === "KeyZ" || code === "Z" || code === "z";
}

function isEndingRightInput(code: string): boolean {
  return code === "KeyD" || code === "ArrowRight" || code === "D" || code === "d" || code === "right";
}

function getLevel0InstructionText(levelId: string): string {
  if (levelId === "level-0-2") {
    return "↑ SIGNAL GUIDE YOU TO ALL YOU NEED ↑";
  }

  if (levelId === LEVEL0_FINAL_ID) {
    return "↑ OBEY THE SIGNAL...FOREVER? ↑";
  }

  if (isLevel0OnboardingLevel(levelId)) {
    return "↑ OBEY THE SIGNAL!!! ↑";
  }

  return "";
}

function getEndingInstructionText(levelId: string, endingPhase: EndingPhase): string {
  if (!isEndingLevel(levelId) || endingPhase !== "obey") {
    return "";
  }

  return "↑ OBEY THE SIGNAL FOREVER ↑";
}

function getPromptFontProfile(levelId: string): BitmapFontLayoutProfile {
  if (isLevel0OnboardingLevel(levelId) || isEndingLevel(levelId)) {
    return SYMBOL_SEQUENCE_FONT_CONFIGS["12px"];
  }

  return ACTIVE_SIMSUN_ASCII_FONT;
}

function getInstructionFontProfile(levelId: string): BitmapFontLayoutProfile {
  if (isLevel0OnboardingLevel(levelId) || isEndingLevel(levelId)) {
    return SIMSUN_ASCII_FONT_CONFIGS["14px"];
  }

  return ACTIVE_SIMSUN_ASCII_FONT;
}

export class LevelScene extends Phaser.Scene {
  public static readonly KEY = "LevelScene";

  private initResult!: LevelSceneInitResult;
  private inputHandler!: InputHandler;
  private actionController!: DefaultActionController;
  private levelRenderer!: LevelRenderer;
  private entityRenderer!: EntityRenderer;
  private fogRenderer!: FogRenderer;
  private codeRainRenderer!: CodeRainRenderer;
  private collectibleEffectRenderer!: CollectibleEffectRenderer;
  private breadcrumbEffectRenderer!: BreadcrumbEffectRenderer;
  private breadcrumbMessageRenderer!: BreadcrumbMessageRenderer;
  private symbolSequenceRenderer!: SymbolSequenceRenderer;
  private hudRenderer!: HudRenderer;
  private debugOverlay!: DebugOverlay;
  private debugController!: DebugController;
  private keyboardListener: ((event: KeyboardEvent) => void) | null = null;
  private isHandlingInput: boolean = false;
  private inPlayback: boolean = false;
  private playerOverride: GridPoint | null = null;
  private flashPlayer: boolean = false;
  private introInputLocked: boolean = false;
  private steadyFollowEnabled: boolean = false;
  private cameraCurrentCenter: CameraPixelPosition | null = null;
  private cameraMotion: CameraMotionState | null = null;
  private runtimeToken: number = 0;
  private readonly scheduledCalls: SceneTimerHandle[] = [];
  private promptText: LayoutBitmapTextRenderer | null = null;
  private level0TransitionStarted: boolean = false;
  private level0TransitionResolved: boolean = false;
  private level0TransitionElapsed: number = 0;
  private completionTransitionStarted: boolean = false;
  private completionTransitionResolved: boolean = false;
  private completionTransitionElapsed: number = 0;
  private completionTransitionTargetLevelId: string | null = null;
  private level0ScrambledSymbolCount: number = 0;
  private transitionOverlay: Phaser.GameObjects.Rectangle | null = null;
  private transitionIn: LevelSceneTransitionInData | null = null;
  private transitionInOverlay: Phaser.GameObjects.Rectangle | null = null;
  private transitionInElapsed: number = 0;
  private transitionInActive: boolean = false;
  private perfectRevealAnimation: PerfectRevealAnimationState | null = null;
  private endingPhase: EndingPhase = "undo";
  private endingUndoCount: number = 0;
  private endingRightCount: number = 0;
  private endingFadeStarted: boolean = false;
  private endingFadeResolved: boolean = false;
  private endingFadeElapsed: number = 0;
  private endingFadeDurationMs: number = ENDING_FINAL_FADE_OUT_MS;
  private endingFadeTargetLevelId: string | null = null;
  private endingDarknessOverlay: Phaser.GameObjects.Rectangle | null = null;
  private skipTransitionHandler: (() => void) | null = null;
  private levelBgm: Phaser.Sound.BaseSound | null = null;
  private startupBreadcrumbIds: readonly string[] = [];
  private walkSettlePlayback: WalkSettlePlaybackContext | null = null;

  public constructor() {
    super(LevelScene.KEY);
  }

  public init(data: LevelSceneData): void {
    this.cleanupSceneRuntime();
    this.destroyRenderers();
    this.clearDomRoots();
    this.destroyPromptText();

    const assets = getRuntimeSession().getAssets();
    const allowDebugTools = import.meta.env.DEV;
    this.initResult = initializeLevelScene(data.levelId, assets);

    this.inputHandler = new InputHandler({ allowDebugShortcuts: allowDebugTools });
    this.actionController = new DefaultActionController(assets.symbols, {
      allowDebugActions: allowDebugTools,
    });
    this.codeRainRenderer = new CodeRainRenderer(
      this,
      CAMERA_RUNTIME.viewportWidthPx,
      CAMERA_RUNTIME.viewportHeightPx,
    );
    this.levelRenderer = new LevelRenderer(this);
    this.entityRenderer = new EntityRenderer(this);
    this.collectibleEffectRenderer = new CollectibleEffectRenderer(this);
    this.breadcrumbEffectRenderer = new BreadcrumbEffectRenderer(this);
    this.breadcrumbMessageRenderer = new BreadcrumbMessageRenderer(this);
    this.fogRenderer = new FogRenderer(this);
    this.fogRenderer.initMask(
      this.initResult.levelData.grid.width,
      this.initResult.levelData.grid.height,
    );

    const fogMask = this.fogRenderer.getMask();
    if (fogMask !== null) {
      this.levelRenderer.setMask(fogMask);
      this.entityRenderer.setMask(fogMask);
    }

    this.symbolSequenceRenderer = new SymbolSequenceRenderer(this);
    this.hudRenderer = new HudRenderer();
    this.debugOverlay = new DebugOverlay();
    this.debugController = new DebugController();
    this.keyboardListener = null;
    this.isHandlingInput = false;
    this.inPlayback = false;
    this.playerOverride = null;
    this.flashPlayer = false;
    this.introInputLocked = false;
    this.steadyFollowEnabled = false;
    this.cameraCurrentCenter = null;
    this.cameraMotion = null;
    this.promptText = null;
    this.level0TransitionStarted = false;
    this.level0TransitionResolved = false;
    this.level0TransitionElapsed = 0;
    this.completionTransitionStarted = false;
    this.completionTransitionResolved = false;
    this.completionTransitionElapsed = 0;
    this.completionTransitionTargetLevelId = null;
    this.level0ScrambledSymbolCount = 0;
    this.transitionOverlay = null;
    this.transitionIn = data.transitionIn ?? null;
    this.transitionInOverlay = null;
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
    this.perfectRevealAnimation = null;
    this.endingPhase = getInitialEndingPhase(this.initResult.levelData.id);
    this.endingUndoCount = 0;
    this.endingRightCount = 0;
    this.endingFadeStarted = false;
    this.endingFadeResolved = false;
    this.endingFadeElapsed = 0;
    this.endingFadeDurationMs = ENDING_FINAL_FADE_OUT_MS;
    this.endingFadeTargetLevelId = null;
    this.endingDarknessOverlay = null;
    this.skipTransitionHandler = null;
    this.walkSettlePlayback = null;
    this.startupBreadcrumbIds = this.initResult.levelData.breadcrumbs
      .filter(
        (breadcrumb) =>
          breadcrumb.triggerOnLevelStart &&
          this.initResult.levelState.breadcrumbs.local.includes(breadcrumb.id),
      )
      .map((breadcrumb) => breadcrumb.id);
  }

  private destroyRenderers(): void {
    this.codeRainRenderer?.destroy();
    this.collectibleEffectRenderer?.destroy();
    this.breadcrumbEffectRenderer?.destroy();
    this.breadcrumbMessageRenderer?.destroy();
    this.levelRenderer?.destroy();
    this.entityRenderer?.destroy();
    this.fogRenderer?.destroy();
    this.symbolSequenceRenderer?.destroy();
  }

  private destroyPromptText(): void {
    if (this.promptText !== null) {
      this.promptText.destroy();
      this.promptText = null;
    }
  }

  private clearDomRoots(): void {
    const hudRoot = document.querySelector<HTMLElement>("#hud-root");
    if (hudRoot !== null) {
      hudRoot.innerHTML = "";
    }

    const debugRoot = document.querySelector<HTMLElement>("#debug-root");
    if (debugRoot !== null) {
      debugRoot.innerHTML = "";
    }
  }

  public create(): void {
    this.cleanupSceneRuntime();
    this.endingPhase = getInitialEndingPhase(this.initResult.levelData.id);
    this.bindSceneCleanup();
    this.setupCamera();
    this.setupInput();
    this.setupNoteSceneListener();
    this.bindCleanup();
    this.playLevelBgm();
    this.setupPromptText();
    this.renderFrame();
    this.setupEndingDarknessOverlay();
    this.startTransitionIn();
    this.triggerStartupBreadcrumbsIfReady();
  }

  public override update(_time: number, delta: number): void {
    if (delta > 0) {
      this.codeRainRenderer.update(delta);
      this.codeRainRenderer.render();
      this.collectibleEffectRenderer.update(delta);
      this.collectibleEffectRenderer.render();
      this.breadcrumbEffectRenderer.update(delta);
      this.breadcrumbEffectRenderer.render();
      this.breadcrumbMessageRenderer.update(delta);
      this.symbolSequenceRenderer.update(delta);
    }

    this.advanceTransitionIn(delta);
    this.triggerStartupBreadcrumbsIfReady();
    this.advancePerfectRevealAnimation(delta);

    if (this.endingFadeStarted && !this.endingFadeResolved) {
      this.endingFadeElapsed += delta;
      if (this.transitionOverlay !== null) {
        this.transitionOverlay.setAlpha(
          Math.min(1, this.endingFadeElapsed / this.endingFadeDurationMs),
        );
      }

      if (this.endingFadeElapsed >= this.endingFadeDurationMs) {
        this.resolveEndingFade();
      }
      return;
    }

    if (this.level0TransitionStarted && !this.level0TransitionResolved) {
      this.level0TransitionElapsed += delta;

      if (this.level0TransitionElapsed < 1000) {
        if (this.transitionOverlay !== null) {
          this.transitionOverlay.setAlpha(this.level0TransitionElapsed / 1000);
        }
        return;
      }

      if (
        this.transitionOverlay !== null &&
        this.transitionOverlay.fillColor !== 0x000000
      ) {
        this.transitionOverlay.setFillStyle(0x000000, 1);
        this.transitionOverlay.setAlpha(1);
        this.writeTransitionPhase("black");
      }

      if (this.level0TransitionElapsed >= 1500) {
        void this.resolveLevel0Transition();
      }
      return;
    }

    if (this.completionTransitionStarted) {
      this.completionTransitionElapsed += delta;
      const holdMs = this.getCompletionTransitionHoldMs();

      if (this.transitionOverlay !== null) {
        const fadeElapsed = Math.max(
          0,
          this.completionTransitionElapsed - holdMs,
        );
        this.transitionOverlay.setAlpha(
          Math.min(1, fadeElapsed / LEVEL_COMPLETION_FADE_OUT_MS),
        );
      }

      if (
        this.completionTransitionElapsed >=
        holdMs + LEVEL_COMPLETION_FADE_OUT_MS
      ) {
        this.resolveCompletionTransition();
      }
      return;
    }

    if (delta <= 0 || this.initResult.cameraConfig.mode !== "follow") {
      return;
    }

    if (this.advanceCameraMotion(delta)) {
      return;
    }

    this.advanceSteadyFollow();
  }

  private setupPromptText(): void {
    const constraints = this.initResult.runtimeConstraints;
    if (constraints === undefined || constraints.promptText.length === 0) {
      return;
    }

    const { width, height } = this.cameras.main;
    this.promptText = new LayoutBitmapTextRenderer(this, {
      depth: 13,
      tint: 0xffffff,
      fontProfile: getPromptFontProfile(this.initResult.levelData.id),
    });
    this.promptText.render(constraints.promptText, {
      centerX: width / 2,
      y: height - 16,
      verticalAlign: "middle",
    });
  }

  private startTransitionIn(): void {
    const transition = this.transitionIn;
    if (transition === null || transition.durationMs <= 0) {
      return;
    }

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      transition.color === "white" ? 0xffffff : 0x000000,
    );
    overlay.setAlpha(1);
    overlay.setScrollFactor(0);
    overlay.setDepth(1001);

    this.transitionInOverlay = overlay;
    this.transitionInElapsed = 0;
    this.transitionInActive = true;
  }

  private setupEndingDarknessOverlay(): void {
    const alpha = getEndingDarknessAlpha(this.initResult.levelData.id);
    if (alpha <= 0) {
      return;
    }

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
    overlay.setAlpha(alpha);
    overlay.setScrollFactor(0);
    overlay.setDepth(9);
    this.endingDarknessOverlay = overlay;
  }

  private advanceTransitionIn(delta: number): void {
    if (!this.transitionInActive || this.transitionInOverlay === null) {
      return;
    }

    const durationMs = Math.max(1, this.transitionIn?.durationMs ?? 1);
    this.transitionInElapsed += Math.max(0, delta);
    const progress = Math.min(1, this.transitionInElapsed / durationMs);
    this.transitionInOverlay.setAlpha(1 - progress);

    if (progress < 1) {
      return;
    }

    this.transitionInOverlay.destroy();
    this.transitionInOverlay = null;
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
  }

  private getCompletionTransitionHoldMs(): number {
    if (this.perfectRevealAnimation !== null) {
      return this.completionTransitionElapsed + LEVEL_COMPLETION_FADE_OUT_MS + 1;
    }

    return LEVEL_COMPLETION_BURST_HOLD_MS;
  }

  private startPerfectRevealAnimation(): void {
    const layers = this.createPerfectRevealLayers(this.initResult.levelState.player);
    if (layers.length === 0) {
      this.finishPerfectRevealAnimation();
      return;
    }

    const targetSteps = Math.max(1, Math.ceil(PERFECT_REVEAL_TARGET_MS / PERFECT_REVEAL_STEP_MS));
    this.perfectRevealAnimation = {
      layers,
      layersPerStep: Math.max(1, Math.ceil(layers.length / targetSteps)),
      nextLayerIndex: 0,
      elapsedMs: PERFECT_REVEAL_STEP_MS,
      completed: false,
      completedElapsedMs: 0,
    };
    this.advancePerfectRevealAnimation(0);
  }

  private advancePerfectRevealAnimation(delta: number): void {
    const animation = this.perfectRevealAnimation;
    if (animation === null) {
      return;
    }

    if (animation.completed) {
      animation.completedElapsedMs += Math.max(0, delta);
      if (animation.completedElapsedMs >= PERFECT_REVEAL_COMPLETED_HOLD_MS) {
        this.perfectRevealAnimation = null;
      }
      return;
    }

    animation.elapsedMs += Math.max(0, delta);
    while (animation.elapsedMs >= PERFECT_REVEAL_STEP_MS && !animation.completed) {
      animation.elapsedMs -= PERFECT_REVEAL_STEP_MS;
      const endIndex = Math.min(
        animation.layers.length,
        animation.nextLayerIndex + animation.layersPerStep,
      );
      const points = animation.layers.slice(animation.nextLayerIndex, endIndex).flat();
      animation.nextLayerIndex = endIndex;
      this.initResult = {
        ...this.initResult,
        levelState: revealFogPoints(this.initResult.levelState, points),
      };
      this.renderFrame();

      if (animation.nextLayerIndex >= animation.layers.length) {
        this.finishPerfectRevealAnimation();
      }
    }
  }

  private finishPerfectRevealAnimation(): void {
    const allGridPoints = createAllGridPoints(this.initResult.levelData);
    this.initResult = {
      ...this.initResult,
      levelState: {
        ...this.initResult.levelState,
        fog: {
          ...this.initResult.levelState.fog,
          permanentReveal: true,
          visible: allGridPoints,
          explored: allGridPoints,
        },
      },
    };
    if (this.perfectRevealAnimation !== null) {
      this.perfectRevealAnimation.completed = true;
    }
    this.renderFrame();
  }

  private createPerfectRevealLayers(center: GridPoint): readonly (readonly GridPoint[])[] {
    const layers = new Map<number, GridPoint[]>();
    for (let y = 0; y < this.initResult.levelData.grid.height; y += 1) {
      for (let x = 0; x < this.initResult.levelData.grid.width; x += 1) {
        const point = { x, y };
        const distance = Math.abs(point.x - center.x) + Math.abs(point.y - center.y);
        const layer = layers.get(distance) ?? [];
        layer.push(point);
        layers.set(distance, layer);
      }
    }

    return [...layers.entries()]
      .sort(([left], [right]) => left - right)
      .map(([, points]) => points);
  }

  private setupCamera(): void {
    const { cameraConfig } = this.initResult;

    if (cameraConfig.mode === "static") {
      const resetCenter = getCameraResetPixelCenter(cameraConfig);
      this.applyCameraCenter(resetCenter);
      return;
    }

    this.startFollowIntroSequence();
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (keyboard === undefined || keyboard === null) {
      return;
    }

    this.removeKeyboardListener();
    this.keyboardListener = (event: KeyboardEvent) => {
      if (event.code === "Tab") {
        event.preventDefault();
      }

      if (event.repeat) {
        return;
      }

      if (
        this.initResult.levelData.id === LEVEL0_FINAL_ID &&
        !isLevel0FinalAllowedInput(event.code)
      ) {
        return;
      }

      if (isEndingLevel(this.initResult.levelData.id) && !this.isEndingAllowedInput(event.code)) {
        return;
      }

      if (event.code === "Semicolon") {
        if (import.meta.env.DEV) {
          this.fogRenderer.toggleDebugMaskOpacity();
          this.renderFrame();
        }
        return;
      }

      if (this.isHandlingInput) {
        return;
      }

      if (this.transitionInActive) {
        return;
      }

      void this.handleKeyboardInput(event.code);
    };

    keyboard.on("keydown", this.keyboardListener);
  }

  private setupNoteSceneListener(): void {
    const noteScene = this.scene.get(NoteScene.KEY);
    noteScene.events.off("shutdown", this.onNoteSceneClosed, this);
    noteScene.events.on("shutdown", this.onNoteSceneClosed, this);
  }

  private onNoteSceneClosed(): void {
    this.renderFrame();
  }

  private rebuildNoteState(): NoteState {
    const save = getRuntimeSession().getCurrentSave();
    let noteState = createEmptyNoteState();
    for (const [symbol, guess] of Object.entries(save.symbols.notes)) {
      if (guess !== null) {
        noteState = { guesses: { ...noteState.guesses, [symbol]: guess } };
      }
    }
    return noteState;
  }

  private bindCleanup(): void {
    this.events.once("shutdown", () => {
      this.cleanup();
    });
    this.events.once("destroy", () => {
      this.cleanup();
    });
  }

  private cleanup(): void {
    this.removeKeyboardListener();
    this.removeSkipTransitionHandler();
  }

  private removeKeyboardListener(): void {
    const keyboard = this.input.keyboard;
    if (
      keyboard !== undefined &&
      keyboard !== null &&
      this.keyboardListener !== null
    ) {
      keyboard.off("keydown", this.keyboardListener);
    }
    this.keyboardListener = null;
  }

  private removeSkipTransitionHandler(): void {
    if (this.skipTransitionHandler !== null) {
      this.input.keyboard?.off("keydown", this.skipTransitionHandler);
      this.skipTransitionHandler = null;
    }
  }

  private renderFrame(): void {
    const { levelState, levelData } = this.initResult;
    const noteState = this.rebuildNoteState();
    this.initResult = { ...this.initResult, noteState };

    const constraints = this.initResult.runtimeConstraints;
    const hideNonPlayer = constraints?.hideNonPlayerEntities ?? false;

    this.levelRenderer.render({ state: levelState, data: levelData });
    this.entityRenderer.render(
      levelState,
      levelData,
      this.playerOverride ?? undefined,
      this.flashPlayer,
      hideNonPlayer,
      this.getEntityVisibilityOverride(),
    );
    this.fogRenderer.render(levelState, levelData);

    const guessResult = buildGuessSequence(noteState, levelData.symbolSequence);
    const isLevel0 = isLevel0OnboardingLevel(levelData.id);
    const isEnding = isEndingLevel(levelData.id);
    const instructionText = isEnding
      ? getEndingInstructionText(levelData.id, this.endingPhase)
      : getLevel0InstructionText(levelData.id);
    const scrambledIndices = isEnding
      ? this.getEndingScrambledIndices(levelData)
      : this.getLevel0ScrambledIndices(levelData);
    const scramble =
      scrambledIndices.length > 0
        ? {
            scrambledIndices,
            glyphs: LEVEL0_SCRAMBLE_GLYPHS,
            intervalMs: LEVEL0_SCRAMBLE_INTERVAL_MS,
          }
        : undefined;
    this.symbolSequenceRenderer.render(
      levelData,
      guessResult.allUnknown
        ? {
            usePlainText: isLevel0 || isEnding,
            instructionText,
            instructionFontProfile: getInstructionFontProfile(levelData.id),
            scramble,
          }
        : {
            subLabel: guessResult.text,
            usePlainText: isLevel0 || isEnding,
            instructionText,
            instructionFontProfile: getInstructionFontProfile(levelData.id),
            scramble,
          },
    );

  }
  private getNewCollectibleIds(
    previousState: LevelState,
    newState: LevelState,
  ): readonly string[] {
    const prevIds = new Set(previousState.collectibles.local);
    return newState.collectibles.local.filter(
      (id) => !prevIds.has(id),
    );
  }

  private dispatchCollectiblePickup(ids: readonly string[]): void {
    if (ids.length === 0) {
      return;
    }

    const levelData = this.initResult.levelData;
    for (const id of ids) {
      const collectible = levelData.collectibles.find((c) => c.id === id);
      if (collectible === undefined) continue;

      const pixelX = collectible.x * TILE_SIZE + COLLECTIBLE_OFFSET;
      const pixelY = collectible.y * TILE_SIZE + COLLECTIBLE_OFFSET;
      this.collectibleEffectRenderer.spawn(
        pixelX,
        pixelY,
        COLLECTIBLE_SIZE,
        RENDER_PALETTE.entities.collectible,
      );
    }
  }

  private getNewBreadcrumbIds(
    previousState: LevelState,
    newState: LevelState,
  ): readonly string[] {
    const prevIds = new Set(previousState.breadcrumbs.local);
    return newState.breadcrumbs.local.filter(
      (id) => !prevIds.has(id),
    );
  }

  private dispatchBreadcrumbTriggers(
    state: LevelState,
    ids: readonly string[],
  ): void {
    for (const id of ids) {
      const breadcrumb = state.breadcrumbs.active.find((b) => b.id === id);
      if (breadcrumb === undefined) continue;

      const pixelX = breadcrumb.x * TILE_SIZE + BREADCRUMB_OFFSET;
      const pixelY = breadcrumb.y * TILE_SIZE + BREADCRUMB_OFFSET;
      this.breadcrumbEffectRenderer.spawn(
        pixelX,
        pixelY,
        BREADCRUMB_SIZE,
        RENDER_PALETTE.effects.breadcrumbBurstColor,
      );

      if (breadcrumb.behavior) {
        this.breadcrumbMessageRenderer.show(
          breadcrumb.behavior.text,
          breadcrumb.behavior.during,
        );
      }
    }
  }

  private triggerStartupBreadcrumbsIfReady(): void {
    if (this.startupBreadcrumbIds.length === 0 || this.transitionInActive) {
      return;
    }

    this.dispatchBreadcrumbTriggers(this.initResult.levelState, this.startupBreadcrumbIds);
    this.startupBreadcrumbIds = [];
  }

  private getEntityVisibilityOverride(): EntityVisibilityOverride | undefined {
    const context = this.walkSettlePlayback;
    if (context === null) {
      return undefined;
    }

    const forceVisibleCollectibleIds = [...context.pendingCollectibleIds];
    const forceVisibleBreadcrumbIds = [...context.pendingBreadcrumbIds];
    if (
      forceVisibleCollectibleIds.length === 0 &&
      forceVisibleBreadcrumbIds.length === 0
    ) {
      return undefined;
    }

    return {
      forceVisibleCollectibleIds,
      forceVisibleBreadcrumbIds,
    };
  }

  private prepareWalkSettlePlayback(
    playback: Readonly<PlaybackMetadata>,
    collectibleIds: readonly string[],
    breadcrumbIds: readonly string[],
  ): void {
    const displayPoints = [playback.contact, ...(playback.fallPath ?? [])];
    if (displayPoints.length === 0) {
      this.walkSettlePlayback = null;
      return;
    }

    const stepEffects: PlaybackStepEffects[] = displayPoints.map(() => ({
      collectibleIds: [],
      breadcrumbIds: [],
    }));
    const completionEffects: PlaybackStepEffects = {
      collectibleIds: [],
      breadcrumbIds: [],
    };

    const assignEffect = (
      point: GridPoint,
      id: string,
      effectKind: "collectible" | "breadcrumb",
    ): void => {
      const stepIndex = displayPoints.findIndex((candidate) =>
        isSamePoint(candidate, point)
      );
      if (stepIndex >= 0) {
        if (effectKind === "collectible") {
          stepEffects[stepIndex]?.collectibleIds.push(id);
        } else {
          stepEffects[stepIndex]?.breadcrumbIds.push(id);
        }
        return;
      }

      if (effectKind === "collectible") {
        completionEffects.collectibleIds.push(id);
      } else {
        completionEffects.breadcrumbIds.push(id);
      }
    };

    for (const id of collectibleIds) {
      const collectible = this.initResult.levelData.collectibles.find(
        (entry) => entry.id === id,
      );
      if (collectible === undefined) {
        continue;
      }
      assignEffect(collectible, id, "collectible");
    }

    for (const id of breadcrumbIds) {
      const breadcrumb = this.initResult.levelData.breadcrumbs.find(
        (entry) => entry.id === id,
      );
      if (breadcrumb === undefined) {
        continue;
      }
      assignEffect(breadcrumb, id, "breadcrumb");
    }

    this.walkSettlePlayback = {
      stepEffects,
      completionEffects,
      pendingCollectibleIds: new Set(collectibleIds),
      pendingBreadcrumbIds: new Set(breadcrumbIds),
    };
  }

  private consumeWalkSettleStepEffects(index: number): void {
    const context = this.walkSettlePlayback;
    const effects = context?.stepEffects[index];
    if (context === null || effects === undefined) {
      return;
    }

    for (const id of effects.collectibleIds) {
      context.pendingCollectibleIds.delete(id);
    }
    for (const id of effects.breadcrumbIds) {
      context.pendingBreadcrumbIds.delete(id);
    }

    this.dispatchCollectiblePickup(effects.collectibleIds);
    this.dispatchBreadcrumbTriggers(this.initResult.levelState, effects.breadcrumbIds);
  }

  private consumeWalkSettleCompletionEffects(): void {
    const context = this.walkSettlePlayback;
    if (context === null) {
      return;
    }

    for (const id of context.completionEffects.collectibleIds) {
      context.pendingCollectibleIds.delete(id);
    }
    for (const id of context.completionEffects.breadcrumbIds) {
      context.pendingBreadcrumbIds.delete(id);
    }

    this.dispatchCollectiblePickup(context.completionEffects.collectibleIds);
    this.dispatchBreadcrumbTriggers(
      this.initResult.levelState,
      context.completionEffects.breadcrumbIds,
    );
  }

  private async handleKeyboardInput(code: string): Promise<void> {
    if (this.scene.isActive("NoteScene")) {
      return;
    }

    if (this.transitionInActive) {
      return;
    }

    if (
      this.initResult.levelData.id === LEVEL0_FINAL_ID &&
      !isLevel0FinalAllowedInput(code)
    ) {
      return;
    }

    if (isEndingLevel(this.initResult.levelData.id)) {
      this.handleEndingInput(code);
      return;
    }

    const mapping = this.inputHandler.map(code);
    if (this.introInputLocked && mapping.mapped !== null) {
      return;
    }

    const constraints = this.initResult.runtimeConstraints;

    if (constraints?.disableNavigation) {
      if (
        mapping.mapped === "returnToLevelSelect" ||
        mapping.mapped === "openNotes"
      ) {
        return;
      }
    }

    if (this.isHandlingInput) {
    }

    this.isHandlingInput = true;

    try {
      if (mapping.mapped === null) {
        return;
      }

      const previousState = this.initResult.levelState;
      const request = { rawInput: mapping.raw, mappedInput: mapping.mapped };
      const result = this.actionController.dispatch(
        request,
        previousState,
        this.initResult.levelData,
      );

      this.initResult = {
        ...this.initResult,
        levelState: result.resolution.state,
      };
      const justPickedIds = this.getNewCollectibleIds(
        previousState,
        result.resolution.state,
      );
      const justTriggeredBreadcrumbIds = this.getNewBreadcrumbIds(
        previousState,
        result.resolution.state,
      );

      switch (result.signal.kind) {
        case "restart": {
          this.cleanupSceneRuntime();
          this.scene.restart({
            levelId: this.initResult.levelData.id,
            transitionIn: {
              color: "black",
              durationMs: LEVEL_SCENE_RESTART_FADE_IN_MS,
            },
          });
          return;
        }
        case "returnToLevelSelect": {
          this.cleanupSceneRuntime();
          this.scene.start(ESC_TARGET_SCENE);
          return;
        }
        case "toggleDebugFog": {
          this.debugController.toggleDebug();
          break;
        }
        case "openNotes": {
          const save = getRuntimeSession().getCurrentSave();
          if (!save.progression.noteUnlocked) {
            break;
          }
          this.scene.launch(NoteScene.KEY, { notes: save.symbols.notes });
          return;
        }
        case "none":
        default: {
          break;
        }
      }

      if (result.resolution.valid) {
        this.breadcrumbMessageRenderer.notifyAction();
        this.advanceLevel0Scramble(mapping.mapped);
      }

      const playback = result.resolution.playback;
      const shouldDeferWalkSettleEffects =
        playback?.kind === "walk-settle" &&
        playback.blocksInput &&
        playback.durationMs > 0;
      if (shouldDeferWalkSettleEffects && playback !== undefined) {
        this.prepareWalkSettlePlayback(
          playback,
          justPickedIds,
          justTriggeredBreadcrumbIds,
        );
      } else {
        this.walkSettlePlayback = null;
        this.dispatchCollectiblePickup(justPickedIds);
        this.dispatchBreadcrumbTriggers(
          result.resolution.state,
          justTriggeredBreadcrumbIds,
        );
      }

      if (
        playback !== undefined &&
        playback.blocksInput &&
        playback.durationMs > 0
      ) {
        this.startPlayback(playback, () => {
          void this.handleCompletionTransition(previousState);
        });
        return;
      }

      if (await this.handleCompletionTransition(previousState)) {
        return;
      }

      this.renderFrame();
    } finally {
      if (
        !this.inPlayback &&
        !this.level0TransitionStarted &&
        !this.completionTransitionStarted
      ) {
        this.isHandlingInput = false;
      }
    }
  }

  private isEndingAllowedInput(code: string): boolean {
    if (this.endingFadeStarted) {
      return false;
    }

    if (this.endingPhase === "undo") {
      return isEndingUndoInput(code);
    }

    if (this.endingPhase === "obey") {
      return isEndingRightInput(code);
    }

    return false;
  }

  private handleEndingInput(code: string): void {
    if (!this.isEndingAllowedInput(code)) {
      return;
    }

    this.isHandlingInput = true;

    if (this.endingPhase === "undo") {
      this.advanceEndingUndoPhase();
    } else if (this.endingPhase === "obey") {
      this.advanceEndingObeyPhase();
    }

    if (!this.endingFadeStarted) {
      this.isHandlingInput = false;
    }
  }

  private advanceEndingUndoPhase(): void {
    this.endingUndoCount = Math.min(ENDING_SEQUENCE_LENGTH, this.endingUndoCount + 1);
    this.movePlayerBy(-1, "left");

    if (this.endingUndoCount >= ENDING_SEQUENCE_LENGTH) {
      this.endingPhase = "obey";
      this.promptText?.setAlpha(0);
    }

    this.renderFrame();
  }

  private advanceEndingObeyPhase(): void {
    this.endingRightCount = Math.min(ENDING_SEQUENCE_LENGTH, this.endingRightCount + 1);
    this.movePlayerBy(1, "right");
    this.renderFrame();

    if (this.endingRightCount >= ENDING_SEQUENCE_LENGTH) {
      this.startEndingFade();
    }
  }

  private movePlayerBy(deltaX: number, facing: "left" | "right"): void {
    this.initResult = {
      ...this.initResult,
      levelState: {
        ...this.initResult.levelState,
        player: {
          ...this.initResult.levelState.player,
          x: this.initResult.levelState.player.x + deltaX,
          facing,
        },
      },
    };
  }

  private startEndingFade(): void {
    this.endingPhase = "complete";
    this.endingFadeStarted = true;
    this.endingFadeResolved = false;
    this.endingFadeElapsed = 0;
    this.endingFadeTargetLevelId = getNextEndingLevelId(this.initResult.levelData.id);
    this.endingFadeDurationMs =
      this.endingFadeTargetLevelId === null
        ? ENDING_FINAL_FADE_OUT_MS
        : ENDING_INTER_LEVEL_FADE_MS;
    this.isHandlingInput = true;

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
    overlay.setAlpha(0);
    overlay.setScrollFactor(0);
    overlay.setDepth(1000);
    this.transitionOverlay = overlay;
  }

  private resolveEndingFade(): void {
    if (this.endingFadeResolved) {
      return;
    }

    this.endingFadeResolved = true;
    const targetLevelId = this.endingFadeTargetLevelId;
    this.endingFadeTargetLevelId = null;

    if (targetLevelId !== null) {
      preserveLevel0BgmForNextScene = true;
      this.deferSceneStart(() => {
        this.scene.start(LevelScene.KEY, {
          levelId: targetLevelId,
          transitionIn: {
            color: "black",
            durationMs: ENDING_INTER_LEVEL_FADE_MS,
          },
        });
      });
      return;
    }

    preserveLevel0BgmForNextScene = true;
    this.deferSceneStart(() => {
      this.scene.start(EndingScene.KEY);
    });
  }

  private async handleCompletionTransition(previousState: LevelState): Promise<boolean> {
    if (
      previousState.completion.cleared ||
      !this.initResult.levelState.completion.cleared
    ) {
      return false;
    }

    this.isHandlingInput = true;

    if (
      isLevel0FinalTransitionLevel(this.initResult.levelData.id) &&
      !this.level0TransitionStarted
    ) {
      this.renderFrame();
      this.startLevel0Transition();
      return true;
    }

    const onboardingTarget = getLevel0WhiteTransitionTarget(
      this.initResult.levelData.id,
    );
    if (onboardingTarget !== null) {
      this.renderFrame();
      this.startCompletionTransition(onboardingTarget);
      return true;
    }

    const constraints = this.initResult.runtimeConstraints;
    if (constraints?.skipCompletionPersistence !== true) {
      const wasPermanentReveal = this.initResult.levelState.fog.permanentReveal;
      const runtimeSession = getRuntimeSession();
      const saveBeforeCompletion = runtimeSession.getCurrentSave();
      const wasPerfected = saveBeforeCompletion.progression.perfectedLevels.includes(
        this.initResult.levelData.id,
      );
      const save = await runtimeSession.applyLevelCompletion(
        this.initResult.levelData,
        this.initResult.levelState,
      );
      const hasPermanentReveal = save.fog.permanentRevealLevels.includes(
        this.initResult.levelData.id,
      );
      const shouldAnimatePerfectReveal =
        this.initResult.levelData.collectibles.length > 0 &&
        this.initResult.levelState.completion.perfected &&
        !wasPerfected &&
        !wasPermanentReveal &&
        hasPermanentReveal;

      this.initResult = {
        ...this.initResult,
        levelState: shouldAnimatePerfectReveal
          ? this.initResult.levelState
          : synchronizePersistentEffects(
              this.initResult.levelState,
              this.initResult.levelData,
              hasPermanentReveal,
            ),
      };

      if (shouldAnimatePerfectReveal) {
        this.startPerfectRevealAnimation();
      }
    }

    this.renderFrame();
    this.startCompletionTransition(null);
    return true;
  }

  private startPlayback(playback: Readonly<PlaybackMetadata>, onComplete?: () => void): void {
    this.inPlayback = true;

    if (playback.kind === "jump-reach-return") {
      this.playerOverride = playback.contact;
      this.flashPlayer = false;
      this.renderFrame();

      this.scheduleSceneDelay(100, () => {
        this.playerOverride = playback.settled;
        this.renderFrame();

        this.scheduleSceneDelay(100, () => {
          this.playerOverride = null;
          this.renderFrame();
          this.inPlayback = false;
          this.isHandlingInput = false;
          onComplete?.();
        });
      });
    } else if (playback.kind === "walk-settle") {
      this.playerOverride = playback.contact;
      this.flashPlayer = false;
      this.consumeWalkSettleStepEffects(0);
      this.renderFrame();

      const fallPath = playback.fallPath ?? [];
      const stepDelayMs = 60;

      const animateStep = (index: number): void => {
        if (index >= fallPath.length) {
          this.playerOverride = null;
          this.consumeWalkSettleCompletionEffects();
          this.walkSettlePlayback = null;
          this.renderFrame();
          this.inPlayback = false;
          this.isHandlingInput = false;
          onComplete?.();
          return;
        }

        this.scheduleSceneDelay(stepDelayMs, () => {
          this.playerOverride = fallPath[index] ?? null;
          this.consumeWalkSettleStepEffects(index + 1);
          this.renderFrame();
          animateStep(index + 1);
        });
      };

      this.scheduleSceneDelay(80, () => {
        animateStep(0);
      });
    } else if (playback.kind === "invalid-flash") {
      this.playerOverride = null;
      this.flashPlayer = true;
      this.renderFrame();

      this.scheduleSceneDelay(playback.durationMs, () => {
        this.flashPlayer = false;
        this.renderFrame();
        this.inPlayback = false;
        this.isHandlingInput = false;
        onComplete?.();
      });
    } else {
      this.renderFrame();
      this.inPlayback = false;
      this.isHandlingInput = false;
      onComplete?.();
    }
  }

  private bindSceneCleanup(): void {
    this.events.once("shutdown", () => {
      this.cleanupSceneRuntime();
    });
    this.events.once("destroy", () => {
      this.cleanupSceneRuntime();
    });
  }

  private cleanupSceneRuntime(): void {
    this.runtimeToken += 1;
    this.destroyLevelBgm({ preserveLevel0Bgm: preserveLevel0BgmForNextScene });
    this.cancelScheduledCalls();
    this.removeKeyboardListener();
    this.introInputLocked = false;
    this.steadyFollowEnabled = false;
    this.cameraMotion = null;
    this.cameraCurrentCenter = null;
    this.isHandlingInput = false;
    this.inPlayback = false;
    this.playerOverride = null;
    this.flashPlayer = false;
    this.walkSettlePlayback = null;
    this.level0TransitionStarted = false;
    this.level0TransitionResolved = false;
    this.level0TransitionElapsed = 0;
    this.completionTransitionStarted = false;
    this.completionTransitionResolved = false;
    this.completionTransitionElapsed = 0;
    this.completionTransitionTargetLevelId = null;
    this.level0ScrambledSymbolCount = 0;
    if (this.transitionOverlay !== null) {
      this.transitionOverlay.destroy();
      this.transitionOverlay = null;
    }
    if (this.transitionInOverlay !== null) {
      this.transitionInOverlay.destroy();
      this.transitionInOverlay = null;
    }
    if (this.endingDarknessOverlay !== null) {
      this.endingDarknessOverlay.destroy();
      this.endingDarknessOverlay = null;
    }
    this.transitionInElapsed = 0;
    this.transitionInActive = false;
    this.perfectRevealAnimation = null;
    this.endingPhase = "undo";
    this.endingUndoCount = 0;
    this.endingRightCount = 0;
    this.endingFadeStarted = false;
    this.endingFadeResolved = false;
    this.endingFadeElapsed = 0;
    this.endingFadeDurationMs = ENDING_FINAL_FADE_OUT_MS;
    this.endingFadeTargetLevelId = null;
  }

  private playLevelBgm(): void {
    const levelId = this.initResult.levelData.id;
    const bgmKey =
      isLevel0OnboardingLevel(levelId) || isEndingLevel(levelId)
        ? LEVEL0_BGM_KEY
        : LEVEL_BGM_KEY;

    const existingSound = this.sound.get(bgmKey);
    if (existingSound !== null) {
      if (bgmKey === LEVEL0_BGM_KEY && existingSound.isPlaying) {
        existingSound.setVolume(DEFAULT_BGM_VOLUME);
        this.levelBgm = existingSound;
        preserveLevel0BgmForNextScene = false;
        return;
      }

      existingSound.stop();
      existingSound.destroy();
    }

    this.levelBgm = this.sound.add(bgmKey, {
      loop: true,
      volume: DEFAULT_BGM_VOLUME,
    });
    this.levelBgm.play();
    preserveLevel0BgmForNextScene = false;
  }

  private destroyLevelBgm(options: { preserveLevel0Bgm?: boolean } = {}): void {
    if (options.preserveLevel0Bgm) {
      this.levelBgm = null;
      return;
    }

    if (this.levelBgm !== null) {
      this.levelBgm.stop();
      this.levelBgm.destroy();
      this.levelBgm = null;
    }

    for (const bgmKey of [LEVEL0_BGM_KEY, LEVEL_BGM_KEY]) {
      const existingSound = this.sound.get(bgmKey);
      if (existingSound !== null) {
        existingSound.stop();
        existingSound.destroy();
      }
    }
  }

  private startFollowIntroSequence(): void {
    const goalPreviewCenter = computeStaticCameraPosition(
      this.initResult.levelData.exit,
    );
    const initialCenter = getCameraResetPixelCenter(
      this.initResult.cameraConfig,
    );

    this.introInputLocked = true;
    this.steadyFollowEnabled = false;
    this.cameraMotion = null;
    this.applyCameraCenter(goalPreviewCenter);

    this.scheduleSceneDelay(FOLLOW_INTRO_HOLD_MS, () => {
      this.startCameraMotion(initialCenter, FOLLOW_INTRO_MOVE_MS, () => {
        this.introInputLocked = false;
        this.resolvePostIntroCorrection();
      });
    });
  }

  private resolvePostIntroCorrection(): void {
    const currentCenter =
      this.cameraCurrentCenter ??
      getCameraResetPixelCenter(this.initResult.cameraConfig);
    const player = this.getPlayerPixelPosition();

    if (!needsOneShotCorrection(player, currentCenter)) {
      this.steadyFollowEnabled = true;
      return;
    }

    const correctionTarget = this.computeFollowCameraTarget(
      player,
      currentCenter,
    );
    this.startCameraMotion(correctionTarget, FOLLOW_CORRECTION_MOVE_MS, () => {
      this.steadyFollowEnabled = true;
    });
  }

  private startCameraMotion(
    targetCenter: CameraPixelPosition,
    durationMs: number,
    onComplete?: () => void,
  ): void {
    const fromCenter = this.cameraCurrentCenter ?? targetCenter;
    const snappedTarget = this.snapCameraCenter(targetCenter);

    if (
      durationMs <= 0 ||
      (fromCenter.x === snappedTarget.x && fromCenter.y === snappedTarget.y)
    ) {
      this.cameraMotion = null;
      this.applyCameraCenter(snappedTarget);
      onComplete?.();
      return;
    }

    this.cameraMotion = {
      from: fromCenter,
      to: snappedTarget,
      durationMs,
      elapsedMs: 0,
      ...(onComplete === undefined ? {} : { onComplete }),
    };
  }

  private advanceCameraMotion(delta: number): boolean {
    const motion = this.cameraMotion;
    if (motion === null) {
      return false;
    }

    motion.elapsedMs = Math.min(motion.durationMs, motion.elapsedMs + delta);
    const progress =
      motion.durationMs === 0 ? 1 : motion.elapsedMs / motion.durationMs;

    this.applyCameraCenter({
      x: lerpPixelSnap(motion.from.x, motion.to.x, progress),
      y: lerpPixelSnap(motion.from.y, motion.to.y, progress),
    });

    if (motion.elapsedMs < motion.durationMs) {
      return true;
    }

    this.cameraMotion = null;
    motion.onComplete?.();
    return true;
  }

  private advanceSteadyFollow(): void {
    if (!this.steadyFollowEnabled || this.inPlayback) {
      return;
    }

    const currentCenter = this.cameraCurrentCenter;
    if (currentCenter === null) {
      return;
    }

    const targetCenter = this.computeFollowCameraTarget(
      this.getPlayerPixelPosition(),
      currentCenter,
    );

    if (
      targetCenter.x === currentCenter.x &&
      targetCenter.y === currentCenter.y
    ) {
      return;
    }

    this.applyCameraCenter({
      x: this.advanceFollowAxis(currentCenter.x, targetCenter.x),
      y: this.advanceFollowAxis(currentCenter.y, targetCenter.y),
    });
  }

  private advanceFollowAxis(current: number, target: number): number {
    const next = lerpPixelSnap(current, target, STEADY_FOLLOW_LERP_FACTOR);
    if (next !== current) {
      return next;
    }

    if (target === current) {
      return current;
    }

    return current + Math.sign(target - current);
  }

  private computeFollowCameraTarget(
    player: PlayerPixelPosition,
    camera: CameraPixelPosition,
  ): CameraPixelPosition {
    return computeFollowTarget(player, camera);
  }

  private getPlayerPixelPosition(): PlayerPixelPosition {
    return computeStaticCameraPosition(this.initResult.levelState.player);
  }

  private applyCameraCenter(center: CameraPixelPosition): void {
    const snappedCenter = this.snapCameraCenter(center);

    if (
      this.cameraCurrentCenter !== null &&
      this.cameraCurrentCenter.x === snappedCenter.x &&
      this.cameraCurrentCenter.y === snappedCenter.y
    ) {
      return;
    }

    this.cameraCurrentCenter = snappedCenter;
    this.cameras.main.centerOn(snappedCenter.x, snappedCenter.y);
  }

  private snapCameraCenter(center: CameraPixelPosition): CameraPixelPosition {
    return {
      x: Math.round(center.x),
      y: Math.round(center.y),
    };
  }

  private scheduleSceneDelay(delayMs: number, callback: () => void): void {
    const token = this.runtimeToken;
    let handle: SceneTimerHandle;

    handle = this.time.delayedCall(delayMs, () => {
      this.unregisterScheduledCall(handle);
      if (token !== this.runtimeToken) {
        return;
      }

      callback();
    });

    this.scheduledCalls.push(handle);
  }

  private deferSceneStart(callback: () => void): void {
    this.scheduleSceneDelay(0, callback);
  }

  private cancelScheduledCalls(): void {
    while (this.scheduledCalls.length > 0) {
      const handle = this.scheduledCalls.pop();
      handle?.remove?.();
      handle?.destroy?.();
    }
  }

  private unregisterScheduledCall(handle: SceneTimerHandle): void {
    const index = this.scheduledCalls.indexOf(handle);
    if (index >= 0) {
      this.scheduledCalls.splice(index, 1);
    }
  }

  private startCompletionTransition(targetLevelId: string | null): void {
    this.completionTransitionStarted = true;
    this.completionTransitionElapsed = 0;
    this.completionTransitionTargetLevelId = targetLevelId;
    this.isHandlingInput = true;

    const exit = this.initResult.levelData.exit;
    const pixelX = exit.x * TILE_SIZE + COLLECTIBLE_OFFSET;
    const pixelY = exit.y * TILE_SIZE + COLLECTIBLE_OFFSET;
    this.collectibleEffectRenderer.spawn(
      pixelX,
      pixelY,
      COLLECTIBLE_SIZE,
      RENDER_PALETTE.entities.exit,
    );

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0xffffff,
    );
    overlay.setAlpha(0);
    overlay.setScrollFactor(0);
    overlay.setDepth(1000);
    this.transitionOverlay = overlay;

    this.writeTransitionPhase("white");
  }

  private resolveCompletionTransition(): void {
    if (this.completionTransitionResolved) {
      return;
    }
    this.completionTransitionResolved = true;
    this.completionTransitionStarted = false;
    this.completionTransitionElapsed = 0;
    this.isHandlingInput = false;
    const targetLevelId = this.completionTransitionTargetLevelId;
    this.completionTransitionTargetLevelId = null;

    this.writeTransitionPhase("resolved");

    if (targetLevelId !== null) {
      if (isLevel0OnboardingLevel(targetLevelId)) {
        preserveLevel0BgmForNextScene = true;
      }
      this.deferSceneStart(() => {
        this.scene.start(LevelScene.KEY, {
          levelId: targetLevelId,
          transitionIn: {
            color: "white",
            durationMs: LEVEL0_INTER_LEVEL_FADE_IN_MS,
          },
        });
      });
      return;
    }

    this.deferSceneStart(() => {
      this.scene.start(ESC_TARGET_SCENE, {
        route: {
          focus: {
            kind: "afterLevelClear",
            levelId: this.initResult.levelData.id,
          },
        },
        transitionIn: {
          color: "white",
          durationMs: LEVEL_COMPLETE_TO_SELECT_FADE_IN_MS,
        },
      });
    });
  }

  private startLevel0Transition(): void {
    this.level0TransitionStarted = true;
    this.level0TransitionElapsed = 0;

    const { width, height } = this.cameras.main;
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0xff0000,
    );
    overlay.setAlpha(0);
    overlay.setScrollFactor(0);
    overlay.setDepth(1000);
    this.transitionOverlay = overlay;

    this.writeTransitionPhase("red");

    this.skipTransitionHandler = () => {
      void this.resolveLevel0Transition();
    };
    this.input.keyboard?.on("keydown", this.skipTransitionHandler);
  }

  private advanceLevel0Scramble(mapped: string | null): void {
    if (this.initResult.levelData.id !== LEVEL0_FINAL_ID || mapped !== "right") {
      return;
    }

    this.level0ScrambledSymbolCount = Math.min(
      this.initResult.levelData.symbolSequence.length,
      this.level0ScrambledSymbolCount + 1,
    );
  }

  private getLevel0ScrambledIndices(levelData: LevelData): readonly number[] {
    if (levelData.id !== LEVEL0_FINAL_ID || this.level0ScrambledSymbolCount <= 0) {
      return [];
    }

    return Array.from(
      {
        length: Math.min(
          this.level0ScrambledSymbolCount,
          levelData.symbolSequence.length,
        ),
      },
      (_value, index) => index,
    );
  }

  private getEndingScrambledIndices(levelData: LevelData): readonly number[] {
    if (!isEndingLevel(levelData.id) || this.endingPhase !== "undo") {
      return [];
    }

    const scrambledCount = Math.max(
      0,
      levelData.symbolSequence.length - this.endingUndoCount,
    );

    return Array.from(
      { length: scrambledCount },
      (_value, index) => index,
    );
  }

  private async resolveLevel0Transition(): Promise<void> {
    if (this.level0TransitionResolved) return;
    this.level0TransitionResolved = true;

    this.removeSkipTransitionHandler();

    this.writeTransitionPhase("resolved");

    if (this.initResult.levelData.id === LEVEL0_FINAL_ID) {
      const runtimeSession = getRuntimeSession();
      const save = runtimeSession.getCurrentSave();
      if (!save.progression.noteUnlocked) {
        await runtimeSession.updateSave({
          ...save,
          progression: { ...save.progression, noteUnlocked: true },
        });
      }
    }

    this.deferSceneStart(() => {
      this.scene.start(LevelScene.KEY, { levelId: "ch1-01" });
    });
  }

  private writeTransitionPhase(_phase: string): void {
    // Debug transition phase display removed — see AGENTS.md TODO #9.
  }
}
